import { useMutation, useQueryClient } from '@tanstack/react-query';
import api, {
  DOCUMENT_SIGN_MAX_FILE_SIZE_BYTES,
  DOCUMENT_SIGN_SOURCE_MIME_TYPE,
  DocumentSignFileResponse,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface UploadDocumentInput {
  envelopeId: string;
  file: File;
  onProgress?: (loaded: number, total: number) => void;
}

export class DocumentUploadError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
    this.name = 'DocumentUploadError';
  }
}

/**
 * Drives the three-step upload: presign → PUT → confirm. Progress is reported
 * via the optional callback so the UI can show a bar without us having to
 * thread XHR plumbing through a bunch of components. Uses XHR rather than
 * fetch because fetch's body streams don't surface upload progress in the
 * browsers we target.
 */
async function putWithProgress(
  uploadUrl: string,
  file: File,
  onProgress?: (loaded: number, total: number) => void,
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('Content-Type', file.type || DOCUMENT_SIGN_SOURCE_MIME_TYPE);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded, e.total);
    };
    xhr.onerror = () => reject(new DocumentUploadError('NETWORK', 'Upload network error'));
    xhr.onabort = () => reject(new DocumentUploadError('ABORTED', 'Upload aborted'));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new DocumentUploadError('S3_REJECTED', `S3 rejected upload (HTTP ${xhr.status})`));
      }
    };
    xhr.send(file);
  });
}

export function useUploadDocument() {
  const queryClient = useQueryClient();

  return useMutation<DocumentSignFileResponse, Error, UploadDocumentInput>({
    mutationFn: async ({ envelopeId, file, onProgress }) => {
      if (file.type !== DOCUMENT_SIGN_SOURCE_MIME_TYPE) {
        throw new DocumentUploadError('INVALID_MIME', 'Only PDF files are supported');
      }
      if (file.size > DOCUMENT_SIGN_MAX_FILE_SIZE_BYTES) {
        throw new DocumentUploadError(
          'TOO_LARGE',
          `File exceeds the ${DOCUMENT_SIGN_MAX_FILE_SIZE_BYTES / (1024 * 1024)} MB limit`,
        );
      }

      const presign = await api.createDocumentSignUploadUrl(envelopeId, {
        fileName: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
      });

      await putWithProgress(presign.uploadUrl, file, onProgress);

      const result = await api.confirmDocumentSignUpload(envelopeId, {
        fileId: presign.fileId,
        s3Key: presign.s3Key,
        fileName: file.name,
        sizeBytes: file.size,
      });

      return result.file;
    },
    onSuccess: (_data, { envelopeId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.files(envelopeId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelope(envelopeId) });
    },
  });
}

export function useDeleteDocumentFile(envelopeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fileId: string) => {
      await api.deleteDocumentSignFile(envelopeId, fileId);
      return fileId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.files(envelopeId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelope(envelopeId) });
    },
  });
}
